import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { ChefHat, Calendar, ListChecks, Globe2, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'

export function LandingPage() {
  const { isAuthenticated } = useAuth()

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Helmet>
        <title>MyMeals — Planifica, cocina y compra sin esfuerzo</title>
        <meta name="description" content="Planificador semanal, descubrimiento de recetas con traducción, y lista de la compra inteligente." />
        <meta property="og:title" content="MyMeals" />
        <meta property="og:description" content="Planifica tus comidas con IA y simplifica tu compra semanal." />
      </Helmet>

      <header className="sticky top-0 z-10 border-b bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <ChefHat className="h-6 w-6 text-primary" />
            <span className="font-semibold">MyMeals</span>
          </Link>
          <nav className="flex items-center gap-2">
            {isAuthenticated ? (
              <Button asChild>
                <Link to="/dashboard">Ir al panel</Link>
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="outline" className="border-primary/20 text-primary hover:bg-primary/5" asChild>
                  <Link to="/login">Iniciar sesión</Link>
                </Button>
              </div>
            )}
          </nav>
        </div>
      </header>

      <main>
        <section className="mx-auto grid max-w-6xl items-center gap-8 px-4 py-16 md:grid-cols-2 md:py-24">
          <div className="space-y-6">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl font-extrabold tracking-tight md:text-5xl"
            >
              Planifica tus comidas. Ahorra tiempo. Come mejor.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-lg text-muted-foreground"
            >
              MyMeals te ayuda a descubrir recetas, planificar tu semana y generar una lista de la compra consolidada. Todo en minutos.
            </motion.p>
            <div className="flex flex-wrap items-center gap-3">
              <Button size="lg" asChild>
                <Link to="/register">Empieza gratis</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to={isAuthenticated ? '/dashboard' : '/login'}>
                  {isAuthenticated ? 'Ir al panel' : 'Iniciar sesión'}
                </Link>
              </Button>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4" />
              <span>Traducción de recetas y búsquedas en español → inglés</span>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <img
              className="rounded-xl shadow-xl ring-1 ring-black/5"
              alt="Hero MyMeals"
              src="https://images.unsplash.com/photo-1518779578993-ec3579fee39f?q=80&w=1600&auto=format&fit=crop"
            />
          </motion.div>
        </section>

        <section className="bg-white py-14">
          <div className="mx-auto grid max-w-6xl gap-6 px-4 md:grid-cols-3">
            <div className="rounded-lg border bg-card p-6">
              <Calendar className="mb-3 h-6 w-6 text-primary" />
              <h3 className="mb-1 font-semibold">Planificador semanal</h3>
              <p className="text-sm text-muted-foreground">Organiza tus comidas por día y servicio con arrastrar y soltar.</p>
            </div>
            <div className="rounded-lg border bg-card p-6">
              <ListChecks className="mb-3 h-6 w-6 text-primary" />
              <h3 className="mb-1 font-semibold">Lista de la compra</h3>
              <p className="text-sm text-muted-foreground">Ingredientes consolidados por pasillos para comprar en menos tiempo.</p>
            </div>
            <div className="rounded-lg border bg-card p-6">
              <Globe2 className="mb-3 h-6 w-6 text-primary" />
              <h3 className="mb-1 font-semibold">Descubre y traduce</h3>
              <p className="text-sm text-muted-foreground">Búsqueda en español con traducción automática para recetas globales.</p>
            </div>
          </div>
          <div className="mx-auto mt-10 max-w-6xl px-4 text-center">
            <Button size="lg" asChild>
              <Link to="/register">Crear cuenta gratuita</Link>
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-t bg-white py-6">
        <div className="mx-auto max-w-6xl px-4 text-sm text-muted-foreground">
          © {new Date().getFullYear()} MyMeals. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  )
}


